import { useRef, useState } from "react";
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from "framer-motion";
import { Eyebrow } from "./Primitives";
import { FadeUp, EASE } from "./Reveal";
import { ENGINE } from "../../data/content";

const STEPS = ENGINE.steps;

function Track({ active }) {
  const n = STEPS.length;
  const leftFor = (i) => (n === 1 ? 0 : (i / (n - 1)) * 100);
  return (
    <div className="relative h-16 w-full">
      {/* base line — centered on node dots */}
      <div className="absolute left-0 right-0 top-[6px] h-[2px] rounded-full bg-line" />
      <motion.div
        className="absolute left-0 top-[6px] h-[2px] rounded-full bg-brand"
        initial={false}
        animate={{ width: `${leftFor(active)}%` }}
        transition={{ duration: 0.7, ease: [0.4, 0, 0.2, 1] }}
      />
      {/* nodes */}
      {STEPS.map((s, i) => {
        const on = i <= active;
        const isActive = i === active;
        return (
          <div
            key={s.key}
            className="absolute top-0 flex -translate-x-1/2 flex-col items-center"
            style={{ left: `${leftFor(i)}%` }}
          >
            <span
              className={`mt-[2px] h-[10px] w-[10px] rounded-full border-2 transition-colors duration-300 ${
                on ? "border-brand bg-brand" : "border-line bg-page"
              }`}
            />
            <span
              className={`mt-4 hidden whitespace-nowrap font-mono-c text-[0.68rem] uppercase tracking-[0.14em] transition-colors duration-300 md:block ${
                isActive ? "text-content" : "text-mutedc"
              }`}
            >
              {s.key}
            </span>
          </div>
        );
      })}
      {/* settling marker — smooth glide (no bounce), very subtle arrival */}
      <motion.div
        className="absolute top-0 z-10 -translate-x-1/2"
        initial={false}
        animate={{ left: `${leftFor(active)}%` }}
        transition={{ duration: 0.7, ease: [0.4, 0, 0.2, 1] }}
      >
        <span className="relative block h-3.5 w-3.5 rounded-full bg-brand shadow-[0_0_0_5px_rgba(247,208,72,0.12)]">
          <motion.span
            key={active}
            className="absolute inset-0 rounded-full bg-brand"
            initial={{ scale: 1, opacity: 0.28 }}
            animate={{ scale: 2.1, opacity: 0 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
          />
        </span>
      </motion.div>
    </div>
  );
}

export function LoopEngine() {
  const outerRef = useRef(null);
  const [active, setActive] = useState(0);
  const { scrollYProgress } = useScroll({ target: outerRef, offset: ["start start", "end end"] });

  useMotionValueEvent(scrollYProgress, "change", (v) => {
    const idx = Math.min(STEPS.length - 1, Math.max(0, Math.floor(v * STEPS.length)));
    setActive(idx);
  });

  const step = STEPS[active];

  return (
    <section id="process" className="relative">
      {/* ---------- Desktop: pinned one-step-at-a-time ---------- */}
      <div ref={outerRef} className="relative hidden h-[560vh] lg:block">
        <div className="sticky top-0 flex h-screen items-center">
          <div className="mx-auto w-full max-w-[1200px] px-12 xl:px-24">
            <Eyebrow>{ENGINE.eyebrow}</Eyebrow>

            <div className="mt-12">
              <Track active={active} />
            </div>

            <div className="relative mt-16 min-h-[300px]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={step.key}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="flex items-baseline gap-4 font-mono-c text-sm text-accent-c">
                    <span>0{active + 1}</span>
                    <span className="h-px w-10 bg-line" />
                    <span className="text-mutedc">0{STEPS.length}</span>
                  </div>

                  <div className="mt-4 overflow-hidden pb-2">
                    <motion.h3
                      initial={{ y: "100%" }}
                      animate={{ y: "0%" }}
                      transition={{ duration: 0.7, ease: EASE }}
                      className="font-display text-7xl font-light leading-[1.06] tracking-[-0.03em] text-content xl:text-[8.5rem]"
                    >
                      {step.key}
                    </motion.h3>
                  </div>

                  <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: EASE, delay: 0.22 }}
                    className="mt-8 max-w-xl text-xl font-normal leading-relaxed text-content"
                  >
                    {step.desc}
                  </motion.p>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>

      {/* ---------- Mobile: vertical step-through (one reveals after another) ---------- */}
      <div className="px-6 py-24 lg:hidden">
        <Eyebrow>{ENGINE.eyebrow}</Eyebrow>
        <div className="mt-12 flex flex-col">
          {STEPS.map((s, i) => (
            <FadeUp key={s.key} className="relative border-t border-line py-10">
              <div className="flex items-baseline gap-3 font-mono-c text-xs text-accent-c">
                <span>0{i + 1}</span>
                <span className="h-px w-8 bg-line" />
                <span className="text-mutedc">0{STEPS.length}</span>
              </div>
              <h3 className="mt-4 font-display text-6xl font-light leading-[1.06] tracking-[-0.03em] text-content">
                {s.key}
              </h3>
              <p className="mt-5 text-lg font-normal leading-relaxed text-content">{s.desc}</p>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}
